/**
 * One of the person's own reports, in the drawer of «My reports» (bata-platform ADR 1020, ADR 9071):
 * what they sent, where it stands, every move it made (the status and the date, never a team
 * member's name), the thread with the team, and a box to reply while the report is open.
 *
 * **Answering the team's question.** When the team asked for information the report is «Information
 * needed» and the box says so; the reply sends it back to the team («Received»). The platform does
 * that move, in the same step as the message, so the person cannot leave a question answered and
 * the report waiting on them.
 *
 * **Correcting the report.** While the platform says the content may still change (`may_amend`:
 * «Received» or «Information needed»), «Add or correct the report's details» opens the structured
 * fields with what the report holds; the reply then carries only what was changed (`amend`). The
 * fields the report still lacks are listed above the box.
 *
 * The reply's idempotency key is held per decision (the code and the text), so a second press after
 * a dropped connection is one message. A refusal is worded through the problem catalogue and the
 * text stays in the box.
 */

import { Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProblemMessage } from '../../../api';
import { formatDateTime } from '../../../lib/format';
import { Button, Checkbox, FieldShell, FOCUS_RING, Skeleton, useNotify } from '../../../primitives';
import { STRUCTURE_LIMITS, useMyReport, useReplyToMyReport, type MyReportDetail as Detail, type ReportAmendment } from '../../feedback/api';
import { ListField } from '../../feedback/ListField';
import { ReportStatus } from '../../feedback/ReportStatus';
import { FIELDS_OF_KIND, fieldWords, type RubricField } from '../../feedback/rubric';
import { kindWords, useSectionLabel } from '../../feedback/words';

import { CLOSED_STATUSES } from './constants';
import { ContentRow, ReportContentRows } from './ReportContent';
import { ReportHistory, ReportThread } from './ReplyThread';

const TEXTAREA = [
  'border-hairline bg-surface-raised text-ink-primary text-body font-ui w-full rounded-control border px-3 py-2',
  FOCUS_RING,
].join(' ');

export function MyReportDetail({ code }: { readonly code: string }): React.JSX.Element {
  const { t } = useTranslation();
  const report = useMyReport(code);
  if (report.isPending) return <Skeleton rows={6} />;
  if (report.isError) {
    return (
      <div role="alert" className="flex flex-col gap-1">
        <p className="text-body text-status-critical font-semibold">{t('feedback.detail.failed')}</p>
      </div>
    );
  }
  return <Loaded report={report.data} />;
}

function Loaded({ report }: { readonly report: Detail }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const sectionLabel = useSectionLabel();
  const open = !CLOSED_STATUSES.has(report.status);
  return (
    <div className="flex flex-col gap-6" data-my-report="">
      <dl className="flex flex-col">
        <ContentRow labelKey="feedback.reports.col.title"><span translate="no" className="font-semibold">{report.title}</span></ContentRow>
        <ContentRow labelKey="feedback.reports.col.kind">{kindWords(t, report.kind)}</ContentRow>
        <ContentRow labelKey="feedback.reports.col.section">{sectionLabel(report.section_id)}</ContentRow>
        <ContentRow labelKey="feedback.reports.col.status"><ReportStatus status={report.status} /></ContentRow>
        <ContentRow labelKey="feedback.reports.col.date">
          <time dateTime={report.created_at}>{t('feedback.reports.at', { time: formatDateTime(report.created_at, i18n.language, 'UTC') })}</time>
        </ContentRow>
        {report.filed_via === 'optima' && <ContentRow labelKey="feedback.detail.origin">{t('feedback.origin.optima')}</ContentRow>}
        <ReportContentRows report={report} />
      </dl>

      <section aria-labelledby="my-report-history" className="flex flex-col gap-2">
        <h3 id="my-report-history" className="text-label text-ink-primary font-semibold">{t('feedback.history.title')}</h3>
        <ReportHistory transitions={Array.isArray(report.history) ? report.history : []} viewer="author" />
      </section>

      <section aria-labelledby="my-report-thread" className="flex flex-col gap-2">
        <h3 id="my-report-thread" className="text-label text-ink-primary font-semibold">{t('feedback.thread.title')}</h3>
        <ReportThread messages={Array.isArray(report.messages) ? report.messages : []} viewer="author" />
        {open
          ? <AuthorReply report={report} />
          : <p className="text-caption text-ink-secondary">{t('feedback.thread.closed')}</p>}
      </section>
    </div>
  );
}

/** The values an amendment starts from: what the report holds now. */
interface Amendable {
  readonly examples: string;
  readonly expectedFigures: string;
  readonly markets: readonly string[];
  readonly runs: readonly string[];
  readonly criteria: readonly string[];
}

function amendableOf(report: Detail): Amendable {
  return {
    examples: report.examples ?? '',
    expectedFigures: report.expected_figures ?? '',
    markets: report.markets,
    runs: report.affected_runs,
    criteria: report.acceptance_criteria,
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((item, index) => item === b[index]);

/** Only what the person changed, under the names the platform reads; `undefined` when nothing did. */
export function amendmentOf(before: Amendable, after: Amendable, asks: (field: RubricField) => boolean): ReportAmendment | undefined {
  const amendment: Record<string, unknown> = {};
  if (asks('examples') && after.examples.trim() !== before.examples.trim()) amendment['examples'] = after.examples.trim();
  if (asks('expected_figures') && after.expectedFigures.trim() !== before.expectedFigures.trim()) amendment['expected_figures'] = after.expectedFigures.trim();
  if (asks('markets') && !sameList(after.markets, before.markets)) amendment['markets'] = after.markets;
  if (asks('runs') && !sameList(after.runs, before.runs)) amendment['affected_runs'] = after.runs;
  if (asks('acceptance_criteria') && !sameList(after.criteria, before.criteria)) amendment['acceptance_criteria'] = after.criteria;
  return Object.keys(amendment).length === 0 ? undefined : amendment as ReportAmendment;
}

function AuthorReply({ report }: { readonly report: Detail }): React.JSX.Element {
  const { t } = useTranslation();
  const reply = useReplyToMyReport();
  const notify = useNotify();
  const problemText = useProblemMessage();
  const code = report.code;
  const asked = report.status === 'needs_info';
  const fields = FIELDS_OF_KIND[report.kind] ?? FIELDS_OF_KIND.incident;
  const asks = (field: RubricField): boolean => fields.has(field);
  // The content can be changed only while the platform says so (`received`, `needs_info`).
  const mayAmend = report.may_amend && (asks('examples') || asks('expected_figures') || asks('markets') || asks('runs') || asks('acceptance_criteria'));
  const missing = Array.isArray(report.missing) ? report.missing : [];

  const [body, setBody] = useState('');
  const [amending, setAmending] = useState(false);
  const [before] = useState(() => amendableOf(report));
  const [examples, setExamples] = useState(before.examples);
  const [expectedFigures, setExpectedFigures] = useState(before.expectedFigures);
  const [markets, setMarkets] = useState(before.markets);
  const [runs, setRuns] = useState(before.runs);
  const [criteria, setCriteria] = useState(before.criteria);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const amend = amending ? amendmentOf(before, { examples, expectedFigures, markets, runs, criteria }, asks) : undefined;

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const text = body.trim();
    if (text === '' || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await reply(code, { body: text, ...(amend === undefined ? {} : { amend }) });
      setBody('');
      notify({ titleKey: asked ? 'feedback.reply.sentAnswer' : 'feedback.reply.sent', titleValues: { code } });
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const textArea = (labelKey: string, value: string, set: (next: string) => void, max: number): React.JSX.Element => (
    <FieldShell labelKey={labelKey}>
      {({ controlId, describedBy }) => (
        <textarea id={controlId} aria-describedby={describedBy} value={value} onChange={(event) => set(event.target.value)} maxLength={max} rows={3} className={TEXTAREA} />
      )}
    </FieldShell>
  );

  return (
    <form onSubmit={(event) => { void submit(event); }} noValidate className="flex flex-col gap-2" data-author-reply="">
      {asked && <p role="status" className="text-body text-ink-primary font-semibold">{t('feedback.reply.asked')}</p>}
      {missing.length > 0 && (
        <div className="flex flex-col gap-1" data-report-missing="">
          <p className="text-caption text-ink-primary font-semibold">{t('optima.intake.missing')}</p>
          <ul className="text-caption text-ink-primary list-disc ps-4">
            {missing.map((field) => <li key={field}>{fieldWords(t, field)}</li>)}
          </ul>
        </div>
      )}
      <FieldShell labelKey={asked ? 'feedback.reply.answerLabel' : 'feedback.reply.label'} helpKey="feedback.reply.help">
        {({ controlId, describedBy }) => (
          <textarea
            id={controlId}
            aria-describedby={describedBy}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={STRUCTURE_LIMITS.message.max}
            rows={4}
            className={TEXTAREA}
          />
        )}
      </FieldShell>
      {mayAmend && <Checkbox labelKey="feedback.amend.toggle" checked={amending} onCheckedChange={setAmending} />}
      {mayAmend && amending && (
        <div className="border-hairline flex flex-col gap-3 rounded-control border p-3" data-author-amend="">
          <p className="text-caption text-ink-secondary">{t('feedback.amend.help')}</p>
          {asks('examples') && textArea('feedback.examples.label', examples, setExamples, STRUCTURE_LIMITS.examples.max)}
          {asks('markets') && (
            <ListField labelKey="feedback.markets.label" items={markets} onChange={setMarkets} maxItems={STRUCTURE_LIMITS.markets.maxItems} upperCase />
          )}
          {asks('runs') && (
            <ListField labelKey="feedback.runs.label" items={runs} onChange={setRuns} maxItems={STRUCTURE_LIMITS.runs.maxItems} maxLength={STRUCTURE_LIMITS.runs.max} />
          )}
          {asks('expected_figures') && textArea('feedback.expectedFigures.label', expectedFigures, setExpectedFigures, STRUCTURE_LIMITS.expectedFigures.max)}
          {asks('acceptance_criteria') && (
            <ListField
              labelKey="feedback.criteria.label"
              items={criteria}
              onChange={setCriteria}
              maxItems={STRUCTURE_LIMITS.acceptanceCriteria.maxItems}
              maxLength={STRUCTURE_LIMITS.acceptanceCriteria.max}
            />
          )}
        </div>
      )}
      {failure !== null && (
        <div role="alert" className="flex flex-col gap-1">
          <p className="text-body text-status-critical font-semibold">{t('feedback.reply.failed')}</p>
          <p className="text-caption text-ink-secondary">{problemText(failure)}</p>
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" variant="primary" icon={<Send size={14} aria-hidden />} loading={busy} disabled={busy || body.trim() === ''}>
          {t(asked ? 'feedback.reply.sendAnswer' : 'feedback.reply.send')}
        </Button>
      </div>
    </form>
  );
}
